import * as Sentry from "@sentry/react";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { SERVER_URL } from "../types/folderTypes";
import { timezoneHeaders } from "../utils/clientTimeZone";
import { languageHeaders } from "../utils/clientLanguage";

async function callSecureEndpoint(
  serverUrlPath: string,
  body: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Not signed in");
  }
  const idToken = await user.getIdToken(false);
  const correlationId = crypto.randomUUID();

  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}${serverUrlPath}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        ...timezoneHeaders(),
        ...languageHeaders(),
        ...extraHeaders,
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    Sentry.captureException(err, { extra: { path: serverUrlPath, correlationId } });
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "<no body>");
    const error = new Error(`Server error ${res.status}: ${text}`);
    if (res.status >= 500) {
      Sentry.captureException(error, {
        extra: { path: serverUrlPath, correlationId, status: res.status },
      });
    }
    throw error;
  }
  return res;
}
export { callSecureEndpoint };

/**
 * Auth Cloud Functions API
 *
 * Wraps httpsCallable invocations used in OTP and account-management flows.
 */
export const authApi = {
  sendLoginCode: (email: string) =>
    httpsCallable(functions, "sendLoginCode")({ email }),

  /** Fire-and-forget login-visibility event, called on every resolved auth session. */
  logLoginEvent: () =>
    httpsCallable<void, { success: boolean }>(functions, "logLoginEvent")(),

  verifyLoginCode: (email: string, code: string) =>
    httpsCallable<
      { email: string; code: string },
      { success?: boolean; error?: string }
    >(functions, "verifyLoginCode")({ email, code }),

  clearUserData: () =>
    httpsCallable<
      void,
      { success: boolean; gracePeriodDays: number; expiresAt: string }
    >(functions, "clearUserData")(),

  recoverClearedData: () =>
    httpsCallable<
      void,
      {
        success: boolean;
        recoveredAt: string;
        partialFailure: boolean;
        failedCollections: string[];
      }
    >(functions, "recoverClearedData")(),

  sendPasswordResetCode: (email: string) =>
    httpsCallable<
      { email: string },
      { success: boolean; message: string }
    >(functions, "sendPasswordResetCode")({ email }),

  changePassword: (email: string, code: string, newPassword: string) =>
    httpsCallable<
      { email: string; code: string; newPassword: string },
      { success: boolean; customToken?: string; uid: string; twoFactorEnabled: boolean; message: string }
    >(functions, "changePassword")({ email, code, newPassword }),

  verifyTwoFactorLogin: (uid: string, code: string) =>
    httpsCallable<
      { uid: string; code: string },
      { success: boolean; customToken: string }
    >(functions, "verifyTwoFactorLogin")({ uid, code }),

  sendReactivationCode: (email: string) =>
    httpsCallable<
      { email: string },
      { success: boolean; daysRemaining: number; deletedAt: string | null; gracePeriodDays: number }
    >(functions, "sendReactivationCode")({ email }),

  confirmReactivation: (email: string, code: string) =>
    httpsCallable<
      { email: string; code: string },
      {
        success: boolean;
        twoFactorEnabled: boolean;
        customToken: string | null;
        uid: string;
        message: string;
      }
    >(functions, "confirmReactivation")({ email, code }),
};
